/**
 * Base Effect Module - Abstract base class for all animation effect modules
 * All effect modules must extend this class and implement required methods
 */
export class BaseEffectModule {
    constructor(name, options = {}) {
        this.name = name;
        this.version = options.version || '1.0.0';
        this.description = options.description || '';
        this.dependencies = options.dependencies || [];
        this.isEnabled = true;
        
        // Performance tracking
        this.performance = {
            totalUpdates: 0,
            totalTime: 0,
            avgTime: 0,
            maxTime: 0,
            errors: 0
        };
        
        // console.log(`🧩 Effect module initialized: ${this.name} v${this.version}`);
    }
    
    /**
     * Create initial state for an element with this effect
     * MUST be implemented by subclasses
     * @param {Element} element - DOM element
     * @param {Object} config - Effect configuration
     * @returns {Object} Initial state object
     */
    createState(element, config) {
        throw new Error(`${this.name} module must implement createState(element, config)`);
    }
    
    /**
     * Update effect for an element in current frame
     * MUST be implemented by subclasses
     * @param {Element} element - DOM element
     * @param {Object} state - Effect state (from createState)
     * @param {Object} sharedState - Global shared state (mouse, scroll, viewport)
     * @param {number} deltaTime - Time since last frame (ms)
     * @param {Object} cache - Element cache (rect, etc.)
     */
    update(element, state, sharedState, deltaTime, cache) {
        throw new Error(`${this.name} module must implement update(element, state, sharedState, deltaTime, cache)`);
    }
    
    /**
     * Cleanup effect state when element is removed
     * Optional - implement if cleanup is needed
     * @param {Object} state - Effect state to cleanup
     */
    cleanup(state) {
        // Default: no cleanup needed
        // Subclasses can override if they need to cleanup resources
    }
    
    /**
     * Enable/disable the effect
     * @param {boolean} enabled - Enable state
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
        // console.log(`${enabled ? '✅' : '❌'} Effect module ${this.name} ${enabled ? 'enabled' : 'disabled'}`);
    }
    
    /**
     * Check if effect is enabled
     * @returns {boolean}
     */
    getEnabled() {
        return this.isEnabled;
    }
    
    /**
     * Get module information
     * @returns {Object}
     */
    getInfo() {
        return {
            name: this.name,
            version: this.version,
            description: this.description,
            dependencies: this.dependencies,
            enabled: this.isEnabled,
            performance: { ...this.performance }
        };
    }
    
    /**
     * Validate configuration object
     * Override in subclasses for specific validation
     * @param {Object} config - Configuration to validate
     * @returns {boolean} True if valid
     */
    validateConfig(config) {
        return config && typeof config === 'object';
    }
    
    /**
     * Track performance of update operations
     * @param {Function} updateFunction - Function to track
     * @returns {*} Result of update function
     */
    trackPerformance(updateFunction) {
        const startTime = performance.now();
        
        try {
            const result = updateFunction();
            
            const endTime = performance.now();
            const executionTime = endTime - startTime;
            
            // Update performance stats
            this.performance.totalUpdates++;
            this.performance.totalTime += executionTime;
            this.performance.avgTime = this.performance.totalTime / this.performance.totalUpdates;
            this.performance.maxTime = Math.max(this.performance.maxTime, executionTime);
            
            return result;
        } catch (error) {
            this.performance.errors++;
            console.error(`❌ Error in ${this.name} module:`, error);
            throw error;
        }
    }
    
    /**
     * Log performance warning if update is slow
     * @param {number} threshold - Warning threshold in ms (default: 5ms)
     */
    checkPerformance(threshold = 5) {
        if (this.performance.avgTime > threshold) {
            console.warn(`⚠️ Slow effect module: ${this.name} (avg: ${this.performance.avgTime.toFixed(2)}ms)`);
        }
    }
    
    /**
     * Reset performance statistics
     */
    resetPerformance() {
        this.performance = {
            totalUpdates: 0,
            totalTime: 0,
            avgTime: 0,
            maxTime: 0,
            errors: 0
        };
    }
    
    /**
     * Utility: Clamp value between min and max
     * @param {number} value - Value to clamp
     * @param {number} min - Minimum value
     * @param {number} max - Maximum value
     * @returns {number} Clamped value
     */
    clamp(value, min, max) {
        return Math.max(min, Math.min(max, value));
    }
    
    /**
     * Utility: Linear interpolation between two values
     * @param {number} a - Start value
     * @param {number} b - End value
     * @param {number} t - Interpolation factor (0-1)
     * @returns {number} Interpolated value
     */
    lerp(a, b, t) {
        return a + (b - a) * this.clamp(t, 0, 1);
    }
    
    /**
     * Utility: Ease out cubic function
     * @param {number} t - Input value (0-1)
     * @returns {number} Eased value
     */
    easeOutCubic(t) {
        return 1 - Math.pow(1 - t, 3);
    }
    
    /**
     * Utility: Ease in out cubic function
     * @param {number} t - Input value (0-1)
     * @returns {number} Eased value
     */
    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    
    /**
     * Utility: Get default value for a property
     * @param {*} value - Current value
     * @param {*} defaultValue - Default value
     * @returns {*} Value or default
     */
    getDefaultValue(value, defaultValue) {
        return value !== undefined && value !== null ? value : defaultValue;
    }
    
    /**
     * Utility: Check if value is significantly different (for optimization)
     * @param {number} a - First value
     * @param {number} b - Second value
     * @param {number} threshold - Difference threshold (default: 0.001)
     * @returns {boolean} True if significantly different
     */
    isSignificantChange(a, b, threshold = 0.001) {
        return Math.abs(a - b) > threshold;
    }
    
    /**
     * Utility: Prepare GSAP properties object
     * @param {Object} properties - Raw properties
     * @returns {Object} GSAP-ready properties
     */
    prepareGSAPProps(properties) {
        const props = {};
        
        // Copy numeric properties
        ['x', 'y', 'rotation', 'scale', 'opacity', 'skewX', 'skewY'].forEach(prop => {
            if (properties[prop] !== undefined) {
                props[prop] = properties[prop];
            }
        });
        
        // Handle special properties
        if (properties.blur !== undefined) {
            props.filter = `blur(${properties.blur}px)`;
        }
        
        if (properties.borderRadius !== undefined) {
            props.borderRadius = `${properties.borderRadius}px`;
        }
        
        if (properties.backgroundColor) {
            props.backgroundColor = properties.backgroundColor;
        }
        
        if (properties.color || properties.color) {
            props.color = properties.color || properties.color;
        }
        
        return props;
    }
}

/**
 * Utility function to create a basic effect module quickly
 * @param {string} name - Module name
 * @param {Object} methods - Methods to implement { createState, update, cleanup }
 * @param {Object} options - Module options
 * @returns {BaseEffectModule} New effect module instance
 */
export function createEffectModule(name, methods, options = {}) {
    class CustomEffectModule extends BaseEffectModule {
        constructor() {
            super(name, options);
        }
        
        createState(element, config) {
            if (methods.createState) {
                return methods.createState.call(this, element, config);
            }
            return {};
        }
        
        update(element, state, sharedState, deltaTime, cache) {
            if (methods.update) {
                return this.trackPerformance(() => 
                    methods.update.call(this, element, state, sharedState, deltaTime, cache)
                );
            }
        }
        
        cleanup(state) {
            if (methods.cleanup) {
                methods.cleanup.call(this, state);
            }
        }
    }
    
    return new CustomEffectModule();
}

export default BaseEffectModule;